import React, { useEffect, useState } from "react";
import { MessageSquareCheck } from "lucide-react";
import "../../Styles/DelayScreen.css";

import { EmptyState, Modal, Panel } from "../../Shared/ScreenComponents.jsx";
import { apiGet, apiPost } from "../../Api/Client.js";
import { formatDate } from "../../Shared/ScreenUtils.jsx";

// Auto-computed, not filed by anyone — worked out from a sprint's own target
// date and from tasks inside it that are already individually overdue, the
// same test the board already runs one task at a time. Shown first, above
// the manual log below: this is the thing nobody had to notice to see.
//
// Triage, not resolution: "Acknowledge" logs why (after talking to the
// stakeholder) and quiets the row, but it does not claim the sprint is
// back on track — only a real status/date change on the sprint itself
// would make this row stop appearing. One step, by direct instruction,
// not a separate ack-then-resolve flow.
function SprintHealthPanel() {
  const [rows, setRows] = useState(null);
  const [recentlyClosed, setRecentlyClosed] = useState([]);
  const [error, setError] = useState("");
  const [ackTarget, setAckTarget] = useState(null); // { row }
  const [ackReason, setAckReason] = useState("");
  const [ackSaving, setAckSaving] = useState(false);
  const [ackError, setAckError] = useState("");

  const load = () => {
    apiGet("/Project/ProjectDelays/sprint-health/")
      .then((res) => { setRows(res.sprints || []); setRecentlyClosed(res.recently_closed || []); })
      .catch((e) => setError(e?.payload?.detail || e?.message || "Could not load sprint health."));
  };
  useEffect(load, []);

  const openAck = (row) => { setAckTarget(row); setAckReason(""); setAckError(""); };

  const submitAck = async () => {
    if (!ackTarget || !ackReason.trim()) return;
    setAckSaving(true);
    setAckError("");
    try {
      await apiPost("/Project/ProjectDelays/acknowledge-sprint/", {
        sprint: ackTarget.sprint_id,
        reason: ackReason.trim(),
      });
      setAckTarget(null);
      load();
    } catch (e) {
      setAckError(e?.payload?.detail || e?.message || "Could not save the acknowledgement.");
    } finally {
      setAckSaving(false);
    }
  };

  const Contributors = ({ row }) => {
    if (!row.contributors?.length) return <span style={{ color: "var(--c-400)" }}>—</span>;
    return (
      <span title={row.contributors.map((c) => c.display_name).join(", ")}>
        {row.contributors.slice(0, 2).map((c) => c.display_name).join(", ")}
        {row.contributors.length > 2 ? ` +${row.contributors.length - 2}` : ""}
      </span>
    );
  };

  return (
    <>
      <Panel title="Sprints Running Behind" subtitle="Worked out automatically from each sprint's own target date and overdue tasks — nothing here was filed by hand. Closed/Paused projects are excluded; closing a sprint here is what takes it off this list.">
        {error && <div className="Auth-Alert" style={{ marginBottom: "1rem" }}>{error}</div>}
        {rows === null && !error ? (
          <p style={{ color: "var(--c-500)", fontSize: 13 }}>Loading…</p>
        ) : rows && rows.length === 0 ? (
          <EmptyState label="Nothing flagged — every open sprint is on track." />
        ) : rows && rows.length > 0 ? (
          <div className="Table-Scroll">
            <table className="Erp-Table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Sprint</th>
                  <th>Manager</th>
                  <th>Who's on it</th>
                  <th>Target</th>
                  <th>Past deadline</th>
                  <th>Tasks overdue</th>
                  <th>Tasks done</th>
                  <th>Triage</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const ack = row.acknowledgement;
                  return (
                    <tr key={row.sprint_id}>
                      <td style={{ fontWeight: 500 }}>{row.project_name}</td>
                      <td>{row.sprint_name}</td>
                      <td>{row.project_manager || <span style={{ color: "var(--c-400)" }}>No manager set</span>}</td>
                      <td><Contributors row={row} /></td>
                      <td>{row.target_on ? formatDate(row.target_on) : "—"}</td>
                      <td>{row.days_late > 0 ? <span style={{ color: "var(--red-600)", fontWeight: 600 }}>{row.days_late}d</span> : "—"}</td>
                      <td>{row.overdue_task_count > 0 ? <span style={{ color: "var(--amber-600)", fontWeight: 600 }}>{row.overdue_task_count}</span> : "—"}</td>
                      <td>{row.task_done_count}/{row.task_count}</td>
                      <td>
                        {ack ? (
                          <span
                            title={`${ack.by || "Someone"}: ${ack.reason}`}
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--c-500)", fontSize: 12.5, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            <MessageSquareCheck size={13} /> Acknowledged
                          </span>
                        ) : (
                          <button className="ButtonSmall" style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={() => openAck(row)}>
                            <MessageSquareCheck size={13} /> Acknowledge
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {ackTarget && (
          <Modal title="Acknowledge delay" onClose={() => setAckTarget(null)}>
            <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--c-600)" }}>
              <strong>{ackTarget.project_name}</strong> / {ackTarget.sprint_name} — {ackTarget.days_late}d past target.
              Log why, after talking to whoever owns it. This doesn't mark the sprint on track — it just records that someone's on it.
            </p>
            <label style={{ display: "block" }}>
              Reason <span style={{ color: "var(--red-600)" }}>*</span>
              <textarea
                className="Mini-Inp"
                value={ackReason}
                onChange={(e) => setAckReason(e.target.value)}
                placeholder="e.g. Client delayed sign-off on the design lock — new target is Friday."
                rows={3}
                style={{ marginTop: 4, width: "100%" }}
                autoFocus
              />
            </label>
            {ackError && <div className="Auth-Alert" style={{ marginTop: 10 }}>{ackError}</div>}
            <div className="Modal-Actions">
              <button className="Primary-Button" onClick={submitAck} disabled={ackSaving || !ackReason.trim()}>
                {ackSaving ? "Saving…" : "Acknowledge"}
              </button>
              <button className="Soft-Button" onClick={() => setAckTarget(null)}>Cancel</button>
            </div>
          </Modal>
        )}
      </Panel>

      {/* Deliberately separate from the table above, not a tab or a filter on
         it — closing a late sprint already takes it off "Running Behind";
         this is just where the record of why goes, by direct instruction
         ("that should not turn the delay management red if the active delay
         is not there... recently closed delayed sprint can show up as a
         separate section, that is okay"). Only renders once there's
         something to show — an empty history isn't worth a permanent panel. */}
      {recentlyClosed.length > 0 && (
        <div style={{ marginTop: "1.5rem" }}>
          <Panel title="Recently Closed, Was Late" subtitle="Closed sprints that finished past their own target date, most recent first. Nothing to act on here — just the record of what happened.">
            <div className="Table-Scroll">
              <table className="Erp-Table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Sprint</th>
                    <th>Closed</th>
                    <th>Was late by</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {recentlyClosed.map((row) => (
                    <tr key={row.sprint_id}>
                      <td style={{ fontWeight: 500 }}>{row.project_name}</td>
                      <td>{row.sprint_name}</td>
                      <td>{formatDate(row.completed_on)}</td>
                      <td><span style={{ color: "var(--amber-600)", fontWeight: 600 }}>{row.days_late}d</span></td>
                      <td style={{ color: "var(--c-500)", fontSize: 12.5 }}>
                        {row.acknowledgement ? `${row.acknowledgement.by || "Someone"}: ${row.acknowledgement.reason}` : "No reason logged"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}

/* ─── DelayManagementScreen ─────────────────────────────────────── *
 * The manual Project/Task/Employee delay form (type, item, days, reason,
 * submit/cancel, with view/resolve/delete per row) was retired 2026-10-07 —
 * it predates the sprint-based system above and was explicitly called out
 * to be dropped: "deprecate all of it in code in backend." The matching
 * backend actions (create/update/destroy/resolve/change-status on
 * ProjectDelayViewSet) now refuse with 410 rather than silently 404ing; see
 * that viewset's docstring. `list`/`retrieve` stay live — the sidebar's
 * "Active delay" indicator and this page's own acknowledgement lookup both
 * still read the same table, just never through the old form again. */
export function DelayManagementScreen() {
  return (
    <div className="DelayPage">
      <SprintHealthPanel />
    </div>
  );
}
